import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DealerAddress,
  DealerDetails,
  DealerText,
  OpenDays,
  OpenHours,
  OpenTime,
  OpenTimeTable,
  OpenTimeWrapper,
  Title,
  VehicleInfoWrapper,
} from './VehicleInfo.styles';
import LocationIcon from '../../assets/icons/LocationIcon';
import { DealerLocationData, Vehicle } from '../../types';
import { SkeletonDealerAddress } from '../Skeleton';
import { selectAppConfig } from '../../redux/appConfigReducer';
import OfferNoVehicle from './Vehicle/OfferNoVehicle';
import OfferSpecificVehicle from './Vehicle/OfferSpecificVehicle';
import VinSpecificVehicle from './Vehicle/VinSpecificVehicle';
import PhoneIcon from '../../assets/icons/PhoneIcon';
import { phoneNumberFormatter } from '../../functions/phoneNumberFormatter';
import TimeIcon from '../../assets/icons/TimeIcon';
import formatOpenHours from '../../functions/formatOpenHours';

interface VehicleInfoProps {
  dealer?: DealerLocationData;
  vehicle?: Vehicle;
  isGeneralForm: boolean;
  isLoadingDealer: boolean;
  isVehicleLoading: boolean;
}

const VehicleInfo: React.FC<VehicleInfoProps> = ({
  dealer,
  vehicle,
  isGeneralForm,
  isLoadingDealer,
  isVehicleLoading,
}) => {
  const { t } = useTranslation();
  const { isOfferPage, offers, presetForm, vehicleId, customDepartmentName, customPhoneNumber } =
    useSelector(selectAppConfig);

  const isCreditBonusOffer = offers?.offerType === 'credit';

  const renderVehicleInfo = useMemo(() => {
    if (isOfferPage && isCreditBonusOffer)
      return (
        <OfferNoVehicle
          offers={offers}
          isOfferPage={isOfferPage}
          isVehicleLoading={isVehicleLoading}
        />
      );
    if (isOfferPage && offers)
      return (
        <OfferSpecificVehicle
          offers={offers}
          isOfferPage={isOfferPage}
          isVehicleLoading={isVehicleLoading}
        />
      );
    if (
      vehicle ||
      isVehicleLoading
      // (!isOfferPage && vehicle && presetForm !== 'tradeIn') ||
      // (presetForm === 'tradeIn' && vehicleId)
    )
      return <VinSpecificVehicle isVehicleLoading={isVehicleLoading} vehicle={vehicle} />;

    return null;
  }, [isCreditBonusOffer, vehicle, isOfferPage, isVehicleLoading, offers]);

  const dealerAddress = {
    street: dealer?.address?.[0],
    cityAndZip: dealer?.address?.[1],
  };

  const dealerTitle = customDepartmentName ?? t('digitalLeads.inputs.dealerDetails');
  const dealerPhone = customPhoneNumber ? phoneNumberFormatter(customPhoneNumber) : dealer?.phone;

  const initialOpenHours = dealer?.openingHours?.departments[0].openingHours ?? null;

  const openedHours = formatOpenHours(initialOpenHours);
  const openingHoursLabel = t('digitalLeads.openingHours');

  return (
    <VehicleInfoWrapper data-testid="vehicle-dealer-info-wrapper">
      {renderVehicleInfo}
      {dealer && (
        <DealerDetails isGeneralForm={isGeneralForm} data-testid="dealer-info">
          <Title>{dealerTitle}</Title>
          <DealerAddress>
            <LocationIcon />
            {isLoadingDealer ? (
              <SkeletonDealerAddress />
            ) : (
              <DealerText data-testid="dealer-info-address">
                <p>{dealerAddress.street}</p>
                <p>{dealerAddress.cityAndZip}</p>
              </DealerText>
            )}
          </DealerAddress>
          <DealerAddress>
            <PhoneIcon />
            {isLoadingDealer ? (
              <SkeletonDealerAddress />
            ) : (
              <DealerText data-testid="dealer-info-phone">
                <p>{dealerPhone}</p>
              </DealerText>
            )}
          </DealerAddress>
          <OpenTimeWrapper>
            <TimeIcon />
            <OpenTimeTable>
              <div>{openingHoursLabel}</div>
              {openedHours &&
                openedHours.map((hours) => {
                  return (
                    <OpenTime key={hours.id}>
                      <OpenDays>{hours.id}:</OpenDays>
                      <OpenHours>
                        {hours.open ? `${hours.openTime} - ${hours.closeTime}` : 'Closed'}
                      </OpenHours>
                    </OpenTime>
                  );
                })}
            </OpenTimeTable>
          </OpenTimeWrapper>
        </DealerDetails>
      )}
    </VehicleInfoWrapper>
  );
};

export default VehicleInfo;
